import {customElement, property, state} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import type {SubmitResponse, SubmitService} from "./formService";
import {resolve} from "../../container";
import {
    DATA_SUBSCRIBE_EVENT,
    FORM_ELEMENT_EVENT,
    FORM_ELEMENT_INIT_EVENT,
    type FormEvent,
    type FormEventData,
    type FormInitEvent,
    type FormStepInitEvent,
    formSubmittedEvent,
    type FormSubscriptionEvent,
    STEP_INIT_EVENT
} from "./formEvents";
import type {PrimitiveType, PropertyMap} from "../../common/utils/objects";
import type {FormSubscription} from "./subscriptions";
import {ScrollService} from "../../common/scroll";
import {SessionStorage} from "../../common/clientStorage";
import {LanguagesService} from "../../common/languages";
import {EopOverlaySpinner} from "../../page/elements/spinner";
import {GLOBAL} from "../../common/globals";
import {FormSessionId} from "./formSessionId";
import {schedule} from "../../common/utils/promises";
import {isDefined, isUndefined} from "../../common/utils/basics";
import jexl from "jexl";
import type {Maybe, Nullable} from "../../bootstrap/common/types";
import {Tracking} from "../../tracking/tracking";
import {TrackingEventName} from "../../tracking/types";
import {trackingButtonInteract, trackingTextlinkInteract} from "../../tracking/trackingAttributes";
import {EopErrorHandler} from "../../common/errorHandler";
import {UnLitElement, WithEopSlots} from "../../common/elements";
import type {FormConfig} from "./formConfig";
import "./formStep";


type Step = {
    id: string;
    label: string;
    nextButtonLabel: string;
    prevButtonLabel: string;
    validate: ((silent?: boolean) => boolean);
    isReachable: boolean;
};

const EMPTY_STEP: Step = {
    id: "",
    label: "",
    nextButtonLabel: "",
    prevButtonLabel: "",
    validate: () => false,
    isReachable: false
};

enum ScrollAfterSubmit {
    TO_TOP = "toTop",
    NONE = "none"
}

@customElement("eop-form")
export class EopForm extends WithEopSlots(UnLitElement) {

    @property({attribute: false})
    public configInput: FormConfig | undefined;

    @state()
    private resultStepId: string;
    @state()
    private currentStepIndex: number;
    @state()
    private steps: Step[];

    private formId: string;
    private configId: string;
    private scrollAfterSubmit: ScrollAfterSubmit;
    private data: PropertyMap;
    private subscriptions: Map<string, FormSubscription[]>;
    private spinner: EopOverlaySpinner;
    private submitService: SubmitService;
    private stepConfigs: FormConfig["steps"];

    public constructor(
        private scrollService: ScrollService = resolve(ScrollService),
        private sessionStorage: SessionStorage = resolve(SessionStorage),
        private formSessionId: FormSessionId = resolve(FormSessionId),
        private languages: LanguagesService = resolve(LanguagesService),
        private tracking: Tracking = resolve(Tracking),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();
        this.data = {};
        this.subscriptions = new Map();
        this.spinner = new EopOverlaySpinner();
        this.resultStepId = "";
        this.steps = [];
        this.currentStepIndex = 0;
        this.stepConfigs = [];
        this.scrollAfterSubmit = ScrollAfterSubmit.TO_TOP;

        this.addEventListener(FORM_ELEMENT_INIT_EVENT, event => {
            const eventData = (event as FormInitEvent).detail;
            eventData.path.addScope(this.id);
            event.stopPropagation();
        });

        this.addEventListener(STEP_INIT_EVENT, event => {
            const eventData = (event as FormStepInitEvent).detail;
            const step = this.steps.find(s => s.id === eventData.step.id);
            if (step) {
                step.validate = eventData.step.validate;
            }
            event.stopPropagation();
        });

        this.addEventListener(FORM_ELEMENT_EVENT, event => {
            const eventData = (event as FormEvent<PrimitiveType>).detail;
            this.newData(eventData);
            event.stopPropagation();
        });

        this.addEventListener(DATA_SUBSCRIBE_EVENT, (event: Event) => {
            const eventData = (event as FormSubscriptionEvent).detail;
            const subscriptions = this.subscriptions.get(eventData.condition) ?? [];
            subscriptions.push(eventData.subscription);
            this.subscriptions.set(eventData.condition, subscriptions);
            event.stopPropagation();
        });
    }

    public connectedCallback(): void {
        super.connectedCallback();
        const config = this.configInput ?? this.readRawData<FormConfig>("form-config");
        this.formId = this.id;
        this.configId = config.configId;
        this.scrollAfterSubmit = (config.scrollAfterSubmit as ScrollAfterSubmit) ?? ScrollAfterSubmit.TO_TOP;
        this.stepConfigs = config.steps ?? [];
        this.steps = this.stepConfigs.map((stepConfig, index) => ({
            id: stepConfig.id,
            label: stepConfig.label,
            nextButtonLabel: stepConfig.nextLabel,
            prevButtonLabel: stepConfig.prevLabel ?? "",
            validate: () => true,
            isReachable: index === 0
        }));
        this.data.id = this.configId;
        this.data.sessionId = this.getSessionId();

        GLOBAL.bodyElement().append(this.spinner);

        const submitServiceId = config.submitServiceId ?? "formService";
        this.submitService = resolve<SubmitService>(submitServiceId);
    }

    public disconnectedCallback(): void {
        this.data = {};
        this.subscriptions = new Map();
        this.resultStepId = "";
        this.steps = [];
        this.currentStepIndex = 0;
        this.spinner.remove();
        super.disconnectedCallback();
    }

    public render(): TemplateResult {
        return html`
            <form>
                ${this.renderWizard()}
                ${this.renderAllSteps()}
                ${this.renderResultSlots()}
                ${this.renderFooterText()}
                ${this.isInResultPhase() ? null : html`
                <div class="eop-form-footer">
                    ${this.renderPrevButton()}
                    ${this.renderNextButton()}
                </div>
            `}
            </form>`;
    }

    private renderAllSteps(): TemplateResult[] {
        return this.stepConfigs.map((config, index) => html`
                <eop-form-step 
                        step-id=${config.id}
                        ?active=${!this.isInResultPhase() && this.currentStepIndex === index}
                        .stepConfig=${config}
                        aria-hidden=${String(this.isInResultPhase() || this.currentStepIndex !== index)}
                ></eop-form-step>
        `);
    }

    private renderResultSlots(): Nullable<TemplateResult> {
        if (!this.resultStepId) {
            return null;
        }
        return html`${this.resolveSlot(this.resultStepId)}`;
    }

    private renderFooterText(): Nullable<TemplateResult> {
        const slot = this.resolveSlot("footer-text");
        if (!slot) {
            return null;
        }
        return html`
            <div class="form-footer-text-content"
                 ?active=${!this.isInResultPhase()}
                 aria-hidden=${String(this.isInResultPhase())}
            >
                ${slot}
            </div>
        `;
    }

    private renderPrevButton(): Nullable<TemplateResult> {
        if (!this.hasPrevStep()) {
            return null;
        }
        return html`
            <div class="prev-button">
                <a class="prev" @click=${this.prevStep} ${trackingTextlinkInteract(this.trackingLabel("formStepBack"))}>
                    ${this.getCurrentStep().prevButtonLabel}
                </a>
            </div>`;
    }

    private renderNextButton(): TemplateResult {
        const trackingLabel = this.hasNextStep()
            ? this.trackingLabel("formStepNext")
            : this.trackingLabel("formSubmit");

        return html`
            <button type="button"
                    class="uni-button primary next"
                    ${trackingButtonInteract(trackingLabel)}
                    @click=${this.nextStep}
            >
                ${this.getCurrentStep().nextButtonLabel}
            </button>
        `;
    }

    private renderWizard(): Nullable<TemplateResult> {
        if (!(this.isMultiStep() && !this.isInResultPhase())) {
            return null;
        }
        return html`
            <div class="form-wizard">
                <a class="wizard-prev"
                   @click=${this.prevStep}
                   ?active=${this.hasPrevStep()}
                   ${trackingTextlinkInteract(this.trackingLabel("formStepWizard", "back"))}
                >
                </a>
                <div class="wizard-steps">
                    ${this.renderWizardSteps()}
                </div>
                <a class="wizard-next"
                   @click=${this.nextStep}
                   ?active=${this.hasNextStep()}
                   ${trackingTextlinkInteract(this.trackingLabel("formStepWizard", "next"))}>
                </a>
            </div>`;
    }

    private renderWizardSteps(): TemplateResult[] {
        return this.steps.map(step => this.renderWizardStep(step));
    }

    private renderWizardStep(step: Step): TemplateResult {
        const handleStepClick = (): void => this.activateStep(step.id);
        return html`
            <a class="wizard-step"
               @click=${handleStepClick}
               ?active=${this.getCurrentStep().id === step.id}
               ?enabled=${step.isReachable}
               ${trackingTextlinkInteract(this.stepTrackingLabel("formStepWizard", this.steps.indexOf(step)))}
            >
                <span class="wizard-text">${step.label}</span>
            </a>
        `;
    }

    private stepTrackingLabel(action: string, targetStepIndex: number): string {
        return this.trackingLabel(action, `jump to step ${targetStepIndex + 1}`);
    }

    private trackingLabel(action: string, details?: string): string {
        const detailsPart = details ? ("(" + details + ")") : "";
        return action + detailsPart + ":" + this.id;
    }

    private getCurrentStep(): Step {
        return this.steps.at(this.currentStepIndex) ?? EMPTY_STEP;
    }

    private isInResultPhase(): boolean {
        return this.resultStepId !== "";
    }

    private async nextStep(): Promise<void> {
        const isValid = this.validateForm();
        if (!isValid) {
            return;
        }

        if (this.hasNextStep()) {
            this.currentStepIndex++;
            this.updateReachability();
        } else {
            await this.submit();
        }

        this.blur();
        if (this.scrollAfterSubmit === ScrollAfterSubmit.TO_TOP) {
            this.scrollToForm();
        }
    }

    private scrollToForm(): void {
        void this.scrollService.scrollToElement(this, 50);
    }

    private hasNextStep(): boolean {
        return this.currentStepIndex < this.steps.length - 1;
    }

    private isMultiStep(): boolean {
        return this.steps.length > 1;
    }

    private hasPrevStep(): boolean {
        return this.currentStepIndex > 0;
    }

    private prevStep(): void {
        this.currentStepIndex--;
        this.scrollToForm();
    }

    private activateStep(stepId: string): void {
        const targetStep = this.steps.find(step => step.id === stepId)!;
        this.currentStepIndex = this.steps.indexOf(targetStep);
    }

    private newData(data: FormEventData<PrimitiveType>): void {
        this.updateData(data.path.slice(1), data.inputValue);
        for (const [customLabel, customValue] of data.customValues) {
            this.updateData(this.customPathFrom(data.path.slice(1), customLabel), customValue);
        }
        for (const [condition, subscriptions] of this.subscriptions.entries()) {
            const result = this.evaluate(condition);
            if (isDefined(result)) {
                subscriptions.forEach(subscription => subscription(result));
            }
        }
        this.updateReachability();
    }

    private customPathFrom(path: string[], label: string): string[] {
        const labelPath = path.clone();
        if (path.length > 0) {
            labelPath[labelPath.length - 1] = path.last() + "_" + label;
        }
        return labelPath;
    }

    private evaluate(condition: string): Maybe<boolean> {
        try {
            const result = jexl.evalSync(condition, this.data);
            return typeof result === "boolean" ? result : undefined;
        } catch (e) {
            this.errorHandler.report("invalid condition", e);
            return undefined;
        }
    }

    private updateData(path: string[], value: PrimitiveType): void {
        const last = path.pop()!;
        let currentData = this.data;
        for (const pathElement of path) {
            let nextElement = currentData[pathElement];
            if (isUndefined(nextElement)) {
                nextElement = {};
                currentData[pathElement] = nextElement;
            }
            currentData = nextElement;
        }
        currentData[last] = value;
    }

    private updateReachability(): void {
        const steps = this.steps.clone();
        for (const step of steps) {
            step.isReachable = this.isReachable(step.id);
        }
        this.steps = steps;
    }

    private async submit(): Promise<void> {
        await schedule(this.handleSubmit()).as("form-submit");
    }

    private async handleSubmit(): Promise<void> {
        try {
            const submit = this.submitService.submit({
                lang: this.languages.activeLanguageId(),
                formId: this.id,
                configId: this.configId,
                formData: this.data
            });
            const response = await this.spinner.spinWhile(submit);
            this.handleSubmitResponse(response);
        } catch (e) {
            this.errorHandler.report(e);
            this.handleErrorSubmitResponse("error");
        }
    }

    private handleSubmitResponse(response: SubmitResponse): void {
        if (response.success) {
            this.handleSuccessSubmitResponse();
        } else {
            this.handleErrorSubmitResponse(response.errorId ?? "error");
        }
    }

    private handleSuccessSubmitResponse(): void {
        this.resultStepId = "success";
        this.sessionStorage.remove(this.id);
        this.formSessionId.clear(this.id);
        this.dispatchEvent(formSubmittedEvent(this.id, true));
        this.trackSuccessfulSubmit();
    }

    private trackSuccessfulSubmit(): void {
        this.tracking.emitFormTrackingEvent({
            name: TrackingEventName.FORM_SUCCESS,
            data: {
                name: this.configId
            }
        });
    }

    private handleErrorSubmitResponse(resultStepId: string): void {
        this.resultStepId = resultStepId;
        this.dispatchEvent(formSubmittedEvent(this.id, false));
    }

    private validateForm(): boolean {
        const validate = this.validationFor(this.getCurrentStep().id);
        return validate();
    }

    private isReachable(stepId: string): boolean {
        const validations = [];
        const targetIndex = this.steps.findIndex(step => step.id === stepId);
        if (targetIndex <= this.currentStepIndex) {
            return true;
        }
        for (const step of this.steps.slice(0, targetIndex)) {
            validations.push(step.validate);
        }
        return validations.every(validation => validation(true));
    }

    private validationFor(stepId?: string): ((silent?: boolean) => boolean) {
        return this.steps
                .find(step => step.id === stepId)
                ?.validate
            ?? (() => false);
    }

    private getSessionId(): string {
        return this.formSessionId.get(this.id);
    }
}
